import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';

import { CanUpdate, showWhenPermission } from '@/shared/components/security';
import ScrollableTable from '@/shared/components/table/ScrollableTable';
import api from '@/shared/api';
import ColumnHeader from '@/shared/components/table/ColumnHeader';
import Checkbox from '@/shared/components/input/Checkbox';
import Axios from 'axios';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import handleHttpError, { translateBackendError } from '@/shared/services/HandleHttpError';
import ToastrService from '@/shared/services/ToastrService';
import { injectStateService, StateService } from '@/shared/services/StateService';
import { FaCog, FaFileExcel } from 'react-icons/fa';
import utils from '@/shared/utils/adminpulse';
import config from '@/config';
import PropTypes from 'prop-types';
import YesNoModal from '@/shared/components/YesNoModal';
import SimplePanel from '@/shared/components/SimplePanel';
import SignalRService from '@/shared/services/SignalRService';
import Button from '@/shared/components/Button';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import SendEmailModal from '@/shared/components/email/MultiRelationsMailModal';
import CheckboxListModal from '@/shared/components/CheckboxListModal';
import {
  handleHiddenColumnsSelection,
  initHiddenColumnsState,
} from '@/shared/utils/hiddenColumnsHelper';
import OkCancelModal from '@/shared/components/OkCancelModal';
import messages from './invoiceStreamMessages';
import InvoiceStreamRow from './InvoiceStreamRow';
import InvoiceStreamFilters, { defaultInvoiceStreamFilters } from './InvoiceStreamFilters';
import InvoiceStreamGlobalActionsButton from './InvoiceStreamGlobalActionsButton';
import SetInvoiceScheduleModal from '../overview/modals/SetInvoiceScheduleModal';
import UpdateInvoiceStreamPriceGridModal from './modals/UpdateInvoiceStreamPriceGridModal';
import UpdateInvoiceStreamAmountModal from './modals/UpdateInvoiceStreamAmountModal';
import UpdateInvoiceStreamUserModal from './modals/UpdateInvoiceStreamUserModal';
import ReprocessInvoiceStreamScheduleModal from './modals/ReprocessInvoiceStreamScheduleModal';
import UpdateInvoiceStreamFieldModal from './modals/UpdateInvoiceStreamFieldModal';
import UpdateInvoiceStreamMergeWithModal from './modals/UpdateInvoiceStreamMergeWithModal';
import UpdateRelationTagsModal from '../overview/modals/UpdateRelationTagsModal';
import UpdateRelationTeamModal from '../overview/modals/UpdateRelationTeamModal';

const columnSelectionStorageKey = 'InvoiceStreamOverviewHiddenColumns';

const columnDefinitions = [
  {
    id: 'uniqueIdentifier',
    message: messages.AssignmentUniqueIdentifier,
    visibleByDefault: false,
    enabled: () => hasAssignments(),
  },
  { id: 'invoiceSchedule', message: messages.InvoiceSchedule, visibleByDefault: true },
  { id: 'amount', message: messages.Amount, visibleByDefault: true },
  {
    id: 'mergeWith',
    message: messages.MergeWith,
    visibleByDefault: false,
    enabled: () => hasAssignments(),
  },
  {
    id: 'fixedAmountRegistrationMethod',
    message: messages.FixedAmountRegistrationMethod,
    visibleByDefault: true,
  },
  {
    id: 'advanceRegistrationMethod',
    message: messages.AdvanceRegistrationMethod,
    visibleByDefault: true,
  },
  { id: 'priceGrid', message: messages.PriceGrid, visibleByDefault: true },
];

class InvoiceStreamContainer extends Component {
  constructor(props) {
    super(props);
    const { searchParams = {} } = props;

    const { invoiceStreamFilters: sessionFilters = {}, ...sessionParams } =
      JSON.parse(sessionStorage.getItem('InvoiceStreamOverviewParams')) || {};

    const { hideableColumns, columnStatus } = initHiddenColumnsState(
      columnSelectionStorageKey,
      columnDefinitions,
      props.intl,
    );
    this.state = {
      items: [],
      allSelected: false,
      showEditAmountConfirmationDialog: false,
      showUpdateAmountModal: false,
      showUpdatePriceGridModal: false,
      showUpdateUserModal: false,
      showColumnSelectionModal: false,
      updateViewModel: undefined,
      currentSearchParams: undefined,
      priceGrids: [],
      hideableColumns,
      columnStatus,
      initialSearchParams: {
        // Set default filters
        sortKey: 'relation.displayName',
        pageSize: 50,
        desc: false,
        invoiceStreamFilters: {
          ...defaultInvoiceStreamFilters,
          ...sessionFilters,
          ...searchParams,
        },
        // Override with session storage params
        ...sessionParams,
      },
    };
  }

  componentDidMount() {
    SignalRService.subscribe('invoiceStreamOverviewUpdated', invoiceStream =>
      this.updateInvoiceStreamHandler(invoiceStream),
    );
    SignalRService.subscribe('reloadInvoiceStreamOverview', this.reloadInvoiceStreamOverivew);

    SignalRService.subscribe('jobUpdated', this.jobUpdateHandler);

    const { intl } = this.props;
    const priceGridPromise = api.base.get(`${config.appUrls.api}/api/Price/Grid`);
    Promise.all([priceGridPromise]).then(([{ data: priceGrids }]) => {
      this.setState({
        priceGrids: priceGrids
          .map(x => ({
            key: x.id,
            id: x.id,
            label: x.name === 'Default' ? intl.formatMessage(messages.Default) : x.name,
          }))
          .sort(dynamicSort('label')),
      });
    });
  }

  componentWillUnmount() {
    SignalRService.unsubscribe('invoiceStreamOverviewUpdated', invoiceStream =>
      this.updateInvoiceStreamHandler(invoiceStream),
    );
    SignalRService.unsubscribe('reloadInvoiceStreamOverview', this.reloadInvoiceStreamOverivew);
    SignalRService.unsubscribe('jobUpdated', this.jobUpdateHandler);

    if (this.searchToken && !this.searchToken.token.reason) {
      this.searchToken.cancel('Operation canceled due to leaving page.');
    }
  }
  jobUpdateHandler = job => {
    if (job.jobType === 46 && (job.status === 2 || job.status === 3)) {
      this.reloadInvoiceStreamOverivew();
    }
  };

  updateInvoiceStreamHandler(invoiceStream) {
    this.setState(state => ({
      items: state.items.map(x => (x.id === invoiceStream.id ? { ...x, ...invoiceStream } : x)),
    }));
  }

  loadItems = searchParams => {
    const { allSelected } = this.state;

    sessionStorage.setItem(
      'InvoiceStreamOverviewParams',
      JSON.stringify({
        desc: searchParams.desc,
        sortKey: searchParams.sortKey,
        invoiceStreamFilters: searchParams.invoiceStreamFilters,
      }),
    );
    if (this.searchToken && !this.searchToken.token.reason) {
      this.searchToken.cancel('Operation canceled due to new request.');
    }
    this.searchToken = Axios.CancelToken.source();

    return api.base
      .post('/api/invoicestream/invoicestreamoverview', searchParams.invoiceStreamFilters, {
        params: {
          desc: searchParams.desc,
          sort: searchParams.sortKey,
          page: searchParams.page,
          take: searchParams.pageSize,
        },
        cancelToken: this.searchToken.token,
      })
      .then(({ data: response }) => {
        const newInvoiceStreams = response.data.map(e => ({ ...e, selected: !!allSelected }));

        this.setState(state => ({
          items:
            searchParams.page > 0 ? [...state.items, ...newInvoiceStreams] : [...newInvoiceStreams],
          total: response.total,
          currentSearchParams: searchParams,
        }));

        return response.data;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
      });
  };

  handleSelectAll = () => {
    this.setState(({ items, allSelected }) => {
      const isCurrentlySelected = !allSelected;

      return {
        allSelected: isCurrentlySelected,
        items: items.map(e => ({ ...e, selected: !!isCurrentlySelected })),
      };
    });
  };

  handleSelect = invoiceStream => {
    this.setState(({ items }) => ({
      items: items.map(x => (x.id === invoiceStream.id ? { ...x, selected: !x.selected } : x)),
    }));
  };

  getCountSelectionsFormatted() {
    const { total } = this.state;
    const { intl } = this.props;
    const count = this.getCountSelections();

    return intl.formatMessage(messages.CountRelationsSelected, { count, total });
  }

  getCountSelections() {
    const { allSelected, total, items } = this.state;

    const count = items ? items.filter(x => x.selected !== allSelected).length : 0;

    if (allSelected) {
      return total - count;
    }
    return count;
  }

  filtersChanged = filters => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        invoiceStreamFilters: filters,
      },
    }));
  };

  onUpdateAmount = updateViewModel => {
    const { intl } = this.props;

    return api.base
      .put(`${config.appUrls.api}/api/invoicestream/updateAmount`, updateViewModel)
      .then(
        ({ data }) => {
          ToastrService.success(intl.formatMessage(messages.MsgConfirmChange));
          this.setState(state => ({
            showEditAmountConfirmationDialog: false,
            items: state.items.map(x => {
              if (x.id === data.id) {
                return { ...x, ...data };
              }
              return x;
            }),
          }));
        },
        error => {
          if (
            error.response &&
            error.response.data &&
            error.response.data.code &&
            error.response.data.code === 11052
          ) {
            // the invoicestream has schedule lines that were manually changed. Ask confirmation
            this.setState({ showEditAmountConfirmationDialog: true, updateViewModel });
          } else {
            handleHttpError(intl)(error);
          }
        },
      );
  };
  onUpdatePriceGrid = (invoiceStreamId, priceGridId) => {
    const { intl } = this.props;

    const model = {
      invoiceStreamId,
      priceGridId,
    };
    return api.base.put(`${config.appUrls.api}/api/invoicestream/UpdatePriceGrid`, model).then(
      ({ data }) => {
        ToastrService.success(intl.formatMessage(messages.MsgConfirmChange));
        this.setState(state => ({
          items: state.items.map(x => {
            if (x.id === data.id) {
              return { ...x, ...data };
            }
            return x;
          }),
        }));
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  };
  createExcelReport = () => {
    const { intl } = this.props;
    const { currentSearchParams: searchParams } = this.state;

    const params = { ...this.getSelectionParams(), filters: searchParams.invoiceStreamFilters };

    const culture = intl.locale;

    const url = `/api/invoiceStream/Report`;

    const urlParams = {
      culture,
      connectionId: SignalRService.getConnectionId(),
    };

    this.setState({
      creatingReport: true,
    });

    return api.base
      .post(url, params, { params: urlParams })
      .then(() => {
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
        this.setState({
          creatingReport: false,
        });
      })
      .catch(error => {
        this.setState({
          creatingReport: false,
        });
        const errorObject = utils.arrayBufferToObject(error.response && error.response.data);
        handleHttpError(intl)(errorObject);
      });
  };

  isGlobalActionsDisabled = () => {
    const { items, allSelected } = this.state;
    if (!items || !items.length) return true;
    if (items.some(x => x.selected) || allSelected) return false;

    return true;
  };
  getExampleAmount = () => {
    const { items } = this.state;
    if (!items || !items.length) return undefined;
    const item = items.find(x => x.selected && x.amount) || items.find(x => x.amount);
    if (!item) return undefined;
    return item.amount;
  };

  executeBatchAction = ({ action, parameters }) => {
    if (this.isGlobalActionsDisabled()) return Promise.reject(new Error('invalid'));

    const { intl } = this.props;
    const { currentSearchParams: searchParams } = this.state;
    const { stateService } = this.props;

    let endpoint = '';
    let successMessage = '';
    let params = {};

    switch (action) {
      case 'setinvoiceschedule':
        endpoint = 'SetBatchInvoiceSchedule';
        successMessage = intl.formatMessage(messages.SetInvoiceScheduleQueued);
        params = { invoiceSchedule: parameters };
        break;
      case 'updateamount':
        endpoint = 'UpdateAmount';
        successMessage = intl.formatMessage(messages.GlobalActionStarted);
        params = { amountUpdate: parameters };
        break;
      case 'updatepricegrid':
        endpoint = 'updatepricegrid';
        successMessage = intl.formatMessage(messages.GlobalActionStarted);
        params = parameters;
        break;
      case 'updateusers':
        endpoint = 'BatchUpdateUser';
        successMessage = intl.formatMessage(messages.GlobalActionStarted);
        params = parameters;
        break;
      case 'reprocessSchedule':
        endpoint = 'BatchReprocessSchedule';
        successMessage = intl.formatMessage(messages.GlobalActionStarted);
        params = parameters;
        break;
      case 'addtags':
        endpoint = 'BatchAddTagsToRelations';
        successMessage = intl.formatMessage(messages.GlobalActionStarted);
        params = parameters;
        break;
      case 'processNextSchedule':
        endpoint = 'BatchProcessNextSchedule';
        successMessage = intl.formatMessage(messages.GlobalActionStarted);
        break;
      case 'executeTaskflowBilling':
        endpoint = 'BatchProcessTaskflowBilling';
        successMessage = intl.formatMessage(messages.GlobalActionStarted);
        break;
      case 'updatefield':
        endpoint = 'BatchUpdateField';
        successMessage = intl.formatMessage(messages.GlobalActionStarted);
        params = parameters;
        break;
      case 'updatemerging':
        endpoint = 'BatchUpdateMerging';
        successMessage = intl.formatMessage(messages.GlobalActionStarted);
        params = parameters;
        break;
      case 'teamsupdate':
        endpoint = 'BatchUpdateTeams';
        successMessage = intl.formatMessage(messages.GlobalActionStarted);
        params = parameters;
        break;
      default:
        endpoint = action;
        successMessage = intl.formatMessage(messages.GlobalActionStarted);
        params = parameters;
        break;
    }

    params = {
      ...params,
      ...this.getSelectionParams(),
      filters: searchParams.invoiceStreamFilters,
    };

    const url = `/api/invoiceStream/${endpoint}`;

    return api.base
      .post(url, params)
      .then(({ data }) => {
        this.setState({
          showSetInvoiceScheduleModal: false,
          showUpdateAmountModal: false,
          showUpdatePriceGridModal: false,
          showSendMailModal: false,
          showUpdateUserModal: false,
          showReprocessScheduleModal: false,
          showProcessNextScheduleModal: false,
          showExecuteTaskflowBillingModal: false,
          showUpdateRelationTagsModal: false,
          showUpdateFieldModal: false,
          showUpdateMergeWithModal: false,
          showUpdateTeamModal: false,
        });

        if (data && Array.isArray(data) && data.length > 0) {
          data.forEach(job => {
            const onClick = job ? () => stateService.gotoJob(job.id) : undefined;

            ToastrService.info(successMessage, undefined, onClick);
          });
          return;
        }

        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;

        ToastrService.info(successMessage, undefined, onClick);
      })
      .catch(handleHttpError(intl));
  };

  getSelectionParams = () => {
    const { items, allSelected } = this.state;

    // Get all relation ids that don't match the select all checkbox
    const invoiceStreamIds = items
      ? items.filter(x => (x.selected || false) !== allSelected).map(x => x.id)
      : [];

    return {
      invoiceStreamIds,
      selectAll: allSelected,
    };
  };

  reloadInvoiceStreamOverivew = () => {
    this.setState(state => ({
      initialSearchParams: {
        ...state.currentSearchParams,
        // forces refresh of table
        random: new Date().getTime(),
      },
    }));
  };

  getSelectedRelationLanguages = () =>
    new Promise((resolve, reject) => {
      const { currentSearchParams: searchParams } = this.state;

      const bodyParams = {
        ...this.getSelectionParams(),
        filters: searchParams.invoiceStreamFilters,
      };

      return api.base
        .post(`/api/invoiceStream/languages`, bodyParams)
        .then(({ data }) => {
          resolve(data);
        })
        .catch(error => {
          reject(error);
        });
    });

  onSendEmail = () => {
    const { intl } = this.props;
    this.getSelectedRelationLanguages()
      .then(languages => {
        this.setState({
          showSendMailModal: true,
          relationLanguages: languages,
        });
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  setInvoiceScheduleSelectedRelations = invoiceSchedule =>
    this.executeBatchAction({
      action: 'setinvoiceschedule',
      parameters: invoiceSchedule,
    });
  sendEmailForSelectedRelations = model =>
    this.executeBatchAction({
      action: 'CreateBatchMailJob',
      parameters: model,
    });
  updateInvoiceStreamAmountBatch = amountUpdate =>
    this.executeBatchAction({
      action: 'updateamount',
      parameters: amountUpdate,
    });

  updateInvoiceStreamPriceGridBatch = model =>
    this.executeBatchAction({
      action: 'updatepricegrid',
      parameters: model,
    });

  updateInvoiceStreamUserBatch = model =>
    this.executeBatchAction({
      action: 'updateusers',
      parameters: model,
    });

  reprocessSchedule = model =>
    this.executeBatchAction({
      action: 'reprocessSchedule',
      parameters: model,
    });

  updateRelationTags = model =>
    this.executeBatchAction({
      action: 'addtags',
      parameters: model,
    });
  processNextSchedule = () =>
    this.executeBatchAction({
      action: 'processNextSchedule',
    });

  executeTaskflowBilling = () =>
    this.executeBatchAction({
      action: 'executeTaskflowBilling',
    });

  updateInvoiceStreamFieldBatch = model =>
    this.executeBatchAction({
      action: 'updatefield',
      parameters: model,
    });

  updateInvoiceStreamMergingBatch = model =>
    this.executeBatchAction({
      action: 'updatemerging',
      parameters: model,
    });

  updateRelationTeams = model =>
    this.executeBatchAction({
      action: 'teamsupdate',
      parameters: model,
    });

  onUpdateField = () => {
    const { intl } = this.props;
    this.getSelectedRelationLanguages()
      .then(languages => {
        this.setState({
          showUpdateFieldModal: true,
          relationLanguages: languages,
        });
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  handleColumnSelection = items => {
    this.setState(({ hideableColumns }) => ({
      ...handleHiddenColumnsSelection(
        items,
        hideableColumns,
        columnSelectionStorageKey,
        columnDefinitions,
      ),
      showColumnSelectionModal: false,
    }));
  };

  getInvoiceStreamErrors = (stream, forced = false) => {
    if (!stream || !stream.relation || !stream.relation.id) return;
    if (!forced && typeof stream.errors !== 'undefined' && stream.errors !== null)
      return stream.errors;
    const { intl } = this.props;

    return api.base
      .get(`${config.appUrls.api}/api/invoicestream/Errors/${stream.relation.id}`)
      .then(
        ({ data }) =>
          new Promise(resolve => {
            if (!data) {
              this.setState(
                ({ items }) => ({
                  items: items.map(x =>
                    x.relation.id === stream.relation.id
                      ? { ...x, hasErrors: false, errors: [] }
                      : x,
                  ),
                }),
                () => resolve([]),
              );
            } else {
              let result = [];
              this.setState(
                ({ items }) => ({
                  items: items.map(x => {
                    if (x.relation.id !== stream.relation.id) return x;
                    let errors = data[x.id] ?? [];
                    if (typeof errors === 'string') errors = JSON.parse(errors);

                    if (x.errors && x.errors.length === 0 && errors.length === 0) {
                      if (x.id === stream.id) {
                        result = [];
                      }
                      return x;
                    }

                    const translatedErrors = errors.map(e =>
                      translateBackendError(intl, e, `${e.code}`),
                    );
                    if (x.id === stream.id) {
                      result = translatedErrors;
                    }
                    return { ...x, hasErrors: errors.length > 0, errors: translatedErrors };
                  }),
                }),
                () => resolve(result),
              );
            }
          }),
        handleHttpError(intl),
      );
  };

  render() {
    const {
      initialSearchParams,
      items,
      allSelected,
      showEditAmountConfirmationDialog,
      updateViewModel,
      showSetInvoiceScheduleModal,
      showUpdateAmountModal,
      showUpdatePriceGridModal,
      creatingReport,
      priceGrids,
      showSendMailModal,
      relationLanguages,
      showUpdateUserModal,
      showReprocessScheduleModal,
      showProcessNextScheduleModal,
      showExecuteTaskflowBillingModal,
      showUpdateRelationTagsModal,
      showUpdateFieldModal,
      showUpdateMergeWithModal,
      showUpdateTeamModal,
      showColumnSelectionModal,
      hideableColumns,
      columnStatus,
    } = this.state;
    const { invoiceDialogsSvc } = this.props;

    const { intl } = this.props;
    const hasData = items && items.length > 0;
    const showAssignments = hasAssignments();
    const invoiceScheduleIdFromFilter =
      initialSearchParams?.invoiceStreamFilters?.invoiceScheduleTypes?.length === 1 ?? false
        ? initialSearchParams?.invoiceStreamFilters?.invoiceScheduleTypes[0]
        : null;

    return (
      <div>
        {showSendMailModal && (
          <SendEmailModal
            intl={intl}
            show
            languages={relationLanguages}
            emailContext={13}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showSendMailModal: false,
              }))
            }
            onSave={emailModel => this.sendEmailForSelectedRelations(emailModel)}
          />
        )}
        {showEditAmountConfirmationDialog && (
          <YesNoModal
            show
            title={intl.formatMessage(messages.UpdateAmount)}
            message={intl.formatMessage(messages.UpdateInvoiceStreamAmountConfirmation)}
            onClose={() =>
              this.setState({
                showEditAmountConfirmationDialog: false,
                updateViewModel: null,
              })
            }
            onNo={() =>
              this.onUpdateAmount({
                ...updateViewModel,
                overrideManualChanges: false,
              })
            }
            onSave={() =>
              this.onUpdateAmount({
                ...updateViewModel,
                overrideManualChanges: true,
              })
            }
          />
        )}
        {showSetInvoiceScheduleModal && (
          <SetInvoiceScheduleModal
            intl={intl}
            show
            onClose={() => this.setState({ showSetInvoiceScheduleModal: false })}
            onSave={invoiceSchedule => this.setInvoiceScheduleSelectedRelations(invoiceSchedule)}
          />
        )}
        {showUpdateAmountModal && (
          <UpdateInvoiceStreamAmountModal
            intl={intl}
            show
            onClose={() => this.setState({ showUpdateAmountModal: false })}
            onSave={amountUpdate => this.updateInvoiceStreamAmountBatch(amountUpdate)}
            exampleAmount={this.getExampleAmount()}
          />
        )}
        {showUpdatePriceGridModal && (
          <UpdateInvoiceStreamPriceGridModal
            intl={intl}
            show
            priceGrids={priceGrids}
            onClose={() => this.setState({ showUpdatePriceGridModal: false })}
            onSave={priceGridModel => this.updateInvoiceStreamPriceGridBatch(priceGridModel)}
          />
        )}
        {showUpdateUserModal && (
          <UpdateInvoiceStreamUserModal
            intl={intl}
            show
            onClose={() => this.setState({ showUpdateUserModal: false })}
            onSave={model => this.updateInvoiceStreamUserBatch(model)}
          />
        )}
        {showReprocessScheduleModal && (
          <ReprocessInvoiceStreamScheduleModal
            intl={intl}
            show
            invoiceScheduleId={invoiceScheduleIdFromFilter}
            onClose={() => this.setState({ showReprocessScheduleModal: false })}
            onSave={model => this.reprocessSchedule(model)}
          />
        )}
        {showUpdateRelationTagsModal && (
          <UpdateRelationTagsModal
            intl={intl}
            onlyAllowAdd
            show={showUpdateRelationTagsModal}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showUpdateRelationTagsModal: false,
              }))
            }
            onSave={data => this.updateRelationTags(data)}
          />
        )}
        {showUpdateTeamModal && (
          <UpdateRelationTeamModal
            intl={intl}
            show={showUpdateTeamModal}
            onClose={() => this.setState({ showUpdateTeamModal: false })}
            onSave={data => this.updateRelationTeams(data)}
          />
        )}
        {showUpdateFieldModal && (
          <UpdateInvoiceStreamFieldModal
            intl={intl}
            show
            languages={relationLanguages}
            companyId={
              initialSearchParams?.invoiceStreamFilters?.ownCompanies?.length === 1
                ? initialSearchParams.invoiceStreamFilters.ownCompanies[0]
                : null
            }
            onClose={() => this.setState({ showUpdateFieldModal: false })}
            onSave={model => this.updateInvoiceStreamFieldBatch(model)}
          />
        )}
        {showUpdateMergeWithModal && (
          <UpdateInvoiceStreamMergeWithModal
            intl={intl}
            show
            onClose={() => this.setState({ showUpdateMergeWithModal: false })}
            onSave={model => this.updateInvoiceStreamMergingBatch(model)}
          />
        )}
        {showProcessNextScheduleModal && (
          <OkCancelModal
            intl={intl}
            show
            onClose={() => this.setState({ showProcessNextScheduleModal: false })}
            onSave={() => this.processNextSchedule()}
            title={intl.formatMessage(messages.ProcessNextInvoiceStreamSchedule)}
          >
            <FormattedMessage
              {...messages.BatchProcessNextScheduleConfirm}
              values={{ count: this.getCountSelections() }}
            />
          </OkCancelModal>
        )}
        {showExecuteTaskflowBillingModal && (
          <OkCancelModal
            intl={intl}
            show
            onClose={() => this.setState({ showExecuteTaskflowBillingModal: false })}
            onSave={() => this.executeTaskflowBilling()}
            title={intl.formatMessage(messages.ExecuteTaskflowBilling)}
          >
            <FormattedMessage
              {...messages.ExecuteTaskflowBillingConfirm}
              values={{ count: this.getCountSelections() }}
            />
          </OkCancelModal>
        )}

        {showColumnSelectionModal && (
          <CheckboxListModal
            title={intl.formatMessage(messages.SelectColumns)}
            items={hideableColumns}
            onSave={this.handleColumnSelection}
            onClose={() => this.setState({ showColumnSelectionModal: false })}
            show={showColumnSelectionModal}
            sortByLabel={false}
          />
        )}

        <SimplePanel
          article="5434791012754"
          title={intl.formatMessage(messages.InvoiceScheduleOverview)}
        >
          <div className="info-block container-fluid">
            <InvoiceStreamFilters
              filters={initialSearchParams.invoiceStreamFilters}
              onFilterChange={filters => this.filtersChanged(filters)}
              countSelectedRelations={this.getCountSelectionsFormatted()}
              priceGrids={priceGrids}
            />
          </div>
        </SimplePanel>
        <div className="row btn-row">
          <div className="col-md-6 pull-right content-right">
            <Button
              btnClassName="btn btn-xs btn-default margin10R"
              onClick={() => this.setState({ showColumnSelectionModal: true })}
            >
              <span className="margin5R">
                <FaCog className="react-icon" />
              </span>
              <FormattedMessage {...messages.ColumnConfig} />
            </Button>
            <Button
              btnClassName="btn btn-xs btn-default margin10R"
              onClick={() => this.createExcelReport()}
              disabled={!hasData || creatingReport}
            >
              <span className="margin5R">
                <FaFileExcel className="file-icon" />
              </span>
              {intl.formatMessage(messages.ExportToExcel)}
            </Button>
            <CanUpdate on="relation">
              <InvoiceStreamGlobalActionsButton
                onSetInvoiceSchedule={() => this.setState({ showSetInvoiceScheduleModal: true })}
                onUpdateAmount={() => this.setState({ showUpdateAmountModal: true })}
                onUpdatePriceGrid={() => this.setState({ showUpdatePriceGridModal: true })}
                onSendEmail={this.onSendEmail}
                onUpdateUser={() => this.setState({ showUpdateUserModal: true })}
                onUpdateField={this.onUpdateField}
                onUpdateMergeWith={() => this.setState({ showUpdateMergeWithModal: true })}
                onReprocessSchedule={() => this.setState({ showReprocessScheduleModal: true })}
                onAddRelationLabel={() => this.setState({ showUpdateRelationTagsModal: true })}
                onUpdateTeam={() => this.setState({ showUpdateTeamModal: true })}
                onProcessNextSchedule={() => this.setState({ showProcessNextScheduleModal: true })}
                onExecuteTaskflowBilling={() =>
                  this.setState({ showExecuteTaskflowBillingModal: true })
                }
                disabled={this.isGlobalActionsDisabled()}
              />
            </CanUpdate>
          </div>
        </div>
        <ScrollableTable
          tableClassName="table-grid margin50B"
          loadItems={searchParams => this.loadItems(searchParams)}
          initialSearchParams={initialSearchParams}
          items={items}
          renderRow={invoiceStream => (
            <InvoiceStreamRow
              key={invoiceStream.id}
              invoiceStream={invoiceStream}
              invoiceDialogsSvc={invoiceDialogsSvc}
              intl={intl}
              priceGrids={priceGrids}
              handleSelect={this.handleSelect}
              onUpdatePriceGrid={priceGridId =>
                this.onUpdatePriceGrid(invoiceStream.id, priceGridId)
              }
              onUpdateAmount={stream =>
                this.onUpdateAmount({
                  invoiceStreamId: stream.id,
                  amount: stream.amount,
                  overrideManualChanges: null,
                })
              }
              showAssignments={showAssignments}
              columnStatus={columnStatus}
              loadErrors={this.getInvoiceStreamErrors}
            />
          )}
          noDataLabel={<FormattedMessage {...messages.NoRelationsFound} />}
        >
          <ColumnHeader
            title={
              <Checkbox
                extraClass="margin2L"
                onClick={() => this.handleSelectAll()}
                value={allSelected}
              />
            }
            className="checkbox-column"
          />
          <ColumnHeader
            title={intl.formatMessage(messages.Relation)}
            sortKey="relation.displayName"
          />
          {showAssignments ? (
            <ColumnHeader
              title={intl.formatMessage(messages.Assignment)}
              sortKey="assignmentTemplateName,assignmentName"
            />
          ) : null}
          <ColumnHeader
            title={intl.formatMessage(messages.AssignmentUniqueIdentifier)}
            visible={!!columnStatus.uniqueIdentifier}
            sortKey="uniqueIdentifier"
          />
          <ColumnHeader
            title={intl.formatMessage(messages.InvoiceSchedule)}
            visible={columnStatus.invoiceSchedule}
          />
          <ColumnHeader
            title={intl.formatMessage(messages.Amount)}
            sortKey="amount"
            visible={columnStatus.amount}
          />
          <ColumnHeader
            title={intl.formatMessage(messages.MergeWith)}
            visible={!!columnStatus.mergeWith}
          />
          <ColumnHeader
            title={intl.formatMessage(messages.FixedAmountRegistrationMethod)}
            visible={columnStatus.fixedAmountRegistrationMethod}
          />
          <ColumnHeader
            title={intl.formatMessage(messages.AdvanceRegistrationMethod)}
            visible={columnStatus.advanceRegistrationMethod}
          />
          <ColumnHeader
            title={intl.formatMessage(messages.PriceGrid)}
            visible={columnStatus.priceGrid}
          />
          <ColumnHeader title="" />
        </ScrollableTable>
      </div>
    );
  }
}

InvoiceStreamContainer.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
  searchParams: PropTypes.shape({}),
  invoiceDialogsSvc: PropTypes.shape({}).isRequired,
};
InvoiceStreamContainer.defaultProps = {
  searchParams: undefined,
};
export default showWhenPermission(
  'InvoiceDraft',
  'read',
)(injectStateService(injectIntl(InvoiceStreamContainer)));
